import { CreditsNavChip } from "@/components/features/Credits/CreditsNavChip";
import { useMobileHeader } from "@/containers/MobileHeaderContext";
import { arrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { handleOpenDrawer } from "@/redux/globalState/globalStateSlice";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import { Menu } from "lucide-react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";

/**
 * Fallback titles (nav keys, translated at render) for routes that do not
 * render a HeroSection (and therefore register no header). Matched by longest
 * route prefix; default is the brand name.
 */
const ROUTE_TITLE_FALLBACKS = [
  { prefix: "/news", title: "topNav.news" },
  { prefix: "/meta-rating", title: "topNav.metaRatings" },
  { prefix: "/legal", title: "topNav.legal" },
  { prefix: "/contact", title: "topNav.contact" },
  { prefix: "/who-are-we", title: "topNav.whoAreWe" },
  { prefix: "/creator-academy", title: "topNav.creatorAcademy" },
  { prefix: "/essentials", title: "topNav.essentials" },
  { prefix: "/subscription", title: "topNav.subscription" },
  { prefix: "/sbc/", title: "topNav.sbcSolution" },
  { prefix: "/import", title: "topNav.clubImport" },
  { prefix: "/club-import", title: "topNav.clubImport" },
] as const;

const resolveFallbackTitleKey = (pathname: string) => {
  const match = ROUTE_TITLE_FALLBACKS.filter((r) =>
    pathname.startsWith(r.prefix),
  ).sort((a, b) => b.prefix.length - a.prefix.length)[0];
  return match?.title;
};

export const MobileTopNav = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { header } = useMobileHeader();
  const { pathname } = useLocation();
  const { t } = useTranslation(["nav", "common"]);

  const openDrawer = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(handleOpenDrawer());
  };

  // DetailedPlayer registers a scroll-reactive alternativeHeader that fully
  // replaces the default bar layout.
  if (header?.alternativeHeader) {
    return <>{header.alternativeHeader}</>;
  }

  const back = header?.back;
  // Only pages that register a header (via HeroSection) own their <h1> here.
  // Fallback pages render their own content <h1>, so the nav title must not be a
  // heading — otherwise those pages get a duplicate/competing h1 on mobile.
  const registeredTitle = header?.h1 ?? header?.title;
  const fallbackKey = resolveFallbackTitleKey(pathname);
  const title =
    registeredTitle ?? (fallbackKey ? t(fallbackKey) : t("common:brand"));
  const TitleTag = registeredTitle ? "h1" : "span";
  const visibleDescription = header?.subtitle ?? header?.description;

  return (
    <div className="header flex h-12 flex-row items-center truncate border-b border-edge bg-background text-sm font-medium text-foreground touch-device:gap-2">
      {back && (
        <button
          onClick={back}
          className="size-12 rotate-90 flex items-center justify-center shrink-0 z-20"
        >
          <div className="w-3 h-3 text-foreground-muted">{arrow}</div>
        </button>
      )}
      <div
        className={cn(
          "flex flex-row items-center min-w-0 mr-auto",
          !back && "pl-4",
        )}
      >
        {!back && header?.icon ? (
          <img
            src={header.icon}
            className="w-6 h-6 shrink-0 object-contain"
            alt=""
          />
        ) : (
          !back && (
            <button
              onClick={() => navigate("/")}
              aria-label={t("goHome")}
              className="shrink-0"
            >
              <img
                src={"/logo.png"}
                className="w-6 h-6"
                alt={t("common:brand")}
              />
            </button>
          )
        )}
        <div className={cn("min-w-0", !back && "ml-2")}>
          <TitleTag className="mt-0.5 truncate block">{title}</TitleTag>
          {visibleDescription && (
            <p className="text-foreground-muted truncate sr-only">
              {visibleDescription}
            </p>
          )}
        </div>
      </div>
      <CreditsNavChip compact />
      <button
        className="h-full px-4"
        onClick={openDrawer}
        aria-label={t("openMenu")}
      >
        <Menu className="size-5 text-foreground-muted" />
      </button>
    </div>
  );
};
